// src/components/Navigation.js
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiSearch, FiChevronDown, FiEdit3 } from 'react-icons/fi';
import ProfileButton from './ProfileButton';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import LoginForm from "./LoginForm";
import { useModal } from "../context/Modal";

const NavigationTab = ({ isLoaded }) => {
  const [isOpen, setIsOpen] = useState(false); // Mobile menu state
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false); // About dropdown state
  const user = useSelector((state) => state.session.user);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const aboutDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const headerRef = useRef(null);

  const [headerHeight, setHeaderHeight] = useState(0);
  const { t, i18n } = useTranslation();
  const { setModalContent } = useModal();

  const openLoginModal = () => {
    setModalContent(<LoginForm />);
    setIsOpen(false);
  };
  const menuItems = [
    { key: "menu.today", path: "/devotions/today" },
    { key: "menu.readingPlans", path: "/reading-plans" },
    { key: "menu.bibleReading", path: "/bible-reading" },
    // My Memos and Sermons are user-owned content. When signed out, clicking
    // them opens the login modal in-place instead of routing to an empty page.
    { key: "menu.myMemos", path: "/my-memos", requiresAuth: true },
    { key: "menu.sermons", path: "/my-memos/sermons", requiresAuth: true },
  ];

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
    setAboutDropdownOpen(false); // Close About dropdown when opening the menu
  };

  const toggleAboutDropdown = () => setAboutDropdownOpen((prev) => !prev);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setIsOpen(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        aboutDropdownRef.current &&
        !aboutDropdownRef.current.contains(event.target)
      ) {
        setAboutDropdownOpen(false);
      }

      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update header height for offset spacing
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const newHeight = headerRef.current.offsetHeight;
        setHeaderHeight((prevHeight) => (prevHeight !== newHeight ? newHeight : prevHeight));
      }
    };

    setTimeout(updateHeaderHeight, 0);
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  return (
    <>
      <nav
        ref={headerRef}
        className="fixed top-0 left-0 w-full z-[9999] shadow-[0_4px_24px_rgba(31,41,55,0.08)]"
        style={{
          // Glassmorphism nav: a very light frosted panel so it reads as a
          // pale glass sheet floating over any page tone. Backdrop blur +
          // subtle saturation gives it the frosted look; the bright top edge
          // and the drop shadow give it the "pane above the page" feel.
          background:
            'linear-gradient(180deg, rgba(248,250,252,0.78) 0%, rgba(238,242,247,0.72) 100%)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          borderTop: '1px solid rgba(255,255,255,0.65)',
          borderBottom: '1px solid rgba(255,255,255,0.4)',
        }}
      >
        <div className="max-w-[1440px] mx-auto flex items-center justify-between gap-4 px-4 lg:px-6 h-16">
          {/* Logo: pencil disc + Bible Memos wordmark */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0 group">
            <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center shadow-[inset_-2px_-2px_4px_rgba(0,0,0,0.05),inset_2px_2px_4px_rgba(255,255,255,0.9),2px_3px_8px_rgba(0,0,0,0.08)] group-hover:shadow-[inset_-1px_-1px_3px_rgba(0,0,0,0.05),inset_1px_1px_3px_rgba(255,255,255,0.9),1px_2px_5px_rgba(0,0,0,0.08)] transition-all">
              <FiEdit3 className="text-[#0e1420] text-[18px]" />
            </div>
            <span className="text-xl font-serif font-semibold text-[#0e1420] tracking-tight">
              Bible Memos
            </span>
          </Link>

          {/* Search bar — pronounced inset so it reads as carved into the nav */}
          <div className="hidden md:flex flex-1 max-w-md">
            <div className="relative w-full">
              <FiSearch size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
              <input
                type="text"
                placeholder={
                  (i18n.language || 'en').startsWith('ko')
                    ? "성경 구절 또는 단어 검색 (예: 막 2:5)"
                    : "Search a verse or word (e.g. John 3:16)"
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-11 pr-4 h-10 rounded-full bg-[#eef2f7] border-none text-sm text-[#0e1420] placeholder:text-gray-500 shadow-[inset_3px_3px_6px_rgba(0,0,0,0.10),inset_-4px_-4px_9px_rgba(255,255,255,1)] focus:outline-none focus:shadow-[inset_3px_3px_6px_rgba(0,0,0,0.12),inset_-4px_-4px_9px_rgba(255,255,255,1),0_0_0_2px_rgba(229,116,51,0.35)] transition-shadow"
              />
            </div>
          </div>

          {/* Desktop menu + auth + language */}
          <div className="hidden xl:flex items-center gap-7 flex-shrink-0">
            {menuItems.map((item) => {
              const needsLogin = item.requiresAuth && !user;
              const commonClass =
                'relative text-sm transition-colors py-1.5 whitespace-nowrap text-gray-500 hover:text-[#0e1420] font-medium';
              if (needsLogin) {
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      setAboutDropdownOpen(false);
                      openLoginModal();
                    }}
                    className={commonClass}
                  >
                    {t(item.key)}
                  </button>
                );
              }
              return (
                <NavLink
                  key={item.key}
                  to={item.path}
                  className={({ isActive }) =>
                    `relative text-sm transition-colors py-1.5 whitespace-nowrap ${
                      isActive
                        ? 'text-[#0e1420] font-semibold after:content-[""] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-[3px] after:rounded-full after:bg-[#e57433]'
                        : 'text-gray-500 hover:text-[#0e1420] font-medium'
                    }`
                  }
                  onClick={() => setAboutDropdownOpen(false)}
                >
                  {t(item.key)}
                </NavLink>
              );
            })}

            {/* About Dropdown */}
            <div ref={aboutDropdownRef} className="relative">
              <button
                onClick={toggleAboutDropdown}
                className="about-button flex items-center text-sm font-medium text-gray-500 hover:text-[#0e1420] transition-colors focus:outline-none whitespace-nowrap"
              >
                {t('menu.about')}
                <FiChevronDown
                  className={`ml-1 transition-transform ${aboutDropdownOpen ? 'rotate-180' : 'rotate-0'}`}
                />
              </button>

              {aboutDropdownOpen && (
                <div className="absolute top-full right-0 mt-3 bg-white shadow-lg rounded-xl z-50 py-2 w-52 border border-gray-100">
                  <NavLink
                    to="/about/who-we-are"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0e1420]"
                    onClick={() => setAboutDropdownOpen(false)}
                  >
                    {t('menu.whoWeAre')}
                  </NavLink>
                  <NavLink
                    to="/about/explore-biblememos"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0e1420]"
                    onClick={() => setAboutDropdownOpen(false)}
                  >
                    {t('menu.exploreBibleMemos')}
                  </NavLink>
                </div>
              )}
            </div>

            {/* Language Switcher (existing component) */}
            <LanguageSwitcher />

            {/* Auth: dark "Sign in" pill when logged out, ProfileButton when logged in */}
            {isLoaded &&
              (user ? (
                <ProfileButton user={user} />
              ) : (
                <button
                  type="button"
                  onClick={openLoginModal}
                  className="inline-flex items-center justify-center px-5 h-10 rounded-xl bg-[#0e1420] text-white text-sm font-semibold hover:bg-[#1c2530] active:bg-black shadow-[-2px_-2px_4px_rgba(255,255,255,0.55),3px_4px_10px_rgba(0,0,0,0.28)] active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.5),inset_-2px_-2px_4px_rgba(255,255,255,0.15)] transition-all"
                >
                  {t('menu.signIn')}
                </button>
              ))}
          </div>

          {/* Hamburger */}
          <div className="xl:hidden">
            <button
              onClick={toggleMenu}
              className="focus:outline-none text-[#0e1420] hover:text-[#e57433] transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <FiX size={26} /> : <FiMenu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div ref={mobileMenuRef} className="xl:hidden bg-white shadow-lg fixed top-full left-0 w-full ">
            <div className="flex flex-col space-y-4 px-4 py-2">
              {menuItems.map((item) => {
                const needsLogin = item.requiresAuth && !user;
                if (needsLogin) {
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        setIsOpen(false);
                        setAboutDropdownOpen(false);
                        openLoginModal();
                      }}
                      className="text-left text-gray-800 hover:text-blue-500 transition-colors px-2 py-1 rounded-lg"
                    >
                      {t(item.key)}
                    </button>
                  );
                }
                return (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    className="text-gray-800 hover:text-blue-500 transition-colors px-2 py-1 rounded-lg"
                    onClick={() => {
                      setIsOpen(false);
                      setAboutDropdownOpen(false);
                    }}
                  >
                    {t(item.key)}
                  </NavLink>
                );
              })}

              {/* About Dropdown in Mobile */}
              <div ref={aboutDropdownRef} className="relative">
                <button onClick={toggleAboutDropdown} className="flex items-center text-gray-800 hover:text-blue-500 transition-colors focus:outline-none ml-2">
                  {t('menu.about')}
                  <FiChevronDown className={`ml-1 transition-transform ${aboutDropdownOpen ? "rotate-180" : "rotate-0"}`} />
                </button>

                {aboutDropdownOpen && (
                  <div className="mt-2 bg-slate-100 shadow-lg rounded-md py-2 w-full flex flex-col">
                    <NavLink
                      to="/about/who-we-are"
                      className="block px-4 py-2 text-gray-800 hover:bg-blue-100 hover:text-blue-600"
                      onClick={() => {
                        setAboutDropdownOpen(false);
                        setIsOpen(false);
                      }}
                    >
                      {t('menu.whoWeAre')}
                    </NavLink>
                    <NavLink
                      to="/about/explore-biblememos"
                      className="block px-4 py-2 text-gray-800 hover:bg-blue-100 hover:text-blue-600"
                      onClick={() => {
                        setAboutDropdownOpen(false);
                        setIsOpen(false);
                      }}
                    >
                      {t('menu.exploreBibleMemos')}
                    </NavLink>
                  </div>
                )}
              </div>

              {/* Mobile Language Switcher with onLanguageChange */}
              <LanguageSwitcher mobile={true} onLanguageChange={() => setIsOpen(false)} />

              <ProfileButton user={user} isMenuOpen={isOpen} closeMenu={toggleMenu} />
            </div>
          </div>
        )}

      </nav>

      {/* Spacer to offset fixed header */}
      <div style={{ height: `${headerHeight}px` }}></div>
    </>
  );
};

export default NavigationTab;
